import React from 'react';
import { motion } from 'framer-motion';
import type { KeyAreaTarget, KeyAreaTargetsResponse } from './types';

function statusColor(band?: string | null): string {
  switch (band) {
    case 'on_track':
      return 'bg-emerald-100 text-emerald-800';
    case 'in_progress':
      return 'bg-amber-100 text-amber-800';
    case 'behind':
      return 'bg-rose-100 text-rose-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function ProgressBar({ pct }: { pct?: number | null }) {
  const w = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
      <div
        className="h-full rounded-full bg-[#15736d] transition-all duration-500"
        style={{ width: `${w}%` }}
      />
    </div>
  );
}

interface KeyAreaTargetsPanelProps {
  data: KeyAreaTargetsResponse | null;
  loading: boolean;
  onClose: () => void;
}

const KeyAreaTargetsPanel: React.FC<KeyAreaTargetsPanelProps> = ({
  data,
  loading,
  onClose,
}) => {
  const ka = data?.key_area;
  const targets: KeyAreaTarget[] = data?.targets || [];

  return (
    <motion.aside
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -12 }}
      transition={{ duration: 0.25 }}
      className="w-full lg:w-[340px] xl:w-[380px] shrink-0 rounded-xl border border-gray-200 bg-[#f7fbfa] flex flex-col max-h-[520px]"
    >
      <div className="flex items-start justify-between gap-2 px-3 py-3 border-b border-gray-200/80">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-[#15736d]">
            Key area targets
          </p>
          <h3 className="text-sm font-bold text-gray-900 truncate">
            {ka?.short_label || ka?.name || '…'}
          </h3>
          {ka?.avg_progress_pct != null && (
            <p className="text-xs text-gray-600 mt-0.5">
              Avg {ka.avg_progress_pct}% · {ka.targets_with_data}/{ka.target_count} with data
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1"
          aria-label="Clear key area filter"
        >
          ×
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {loading && (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-white/80" />
            ))}
          </div>
        )}

        {!loading && targets.length === 0 && (
          <p className="text-xs text-gray-500 py-4 text-center">No targets in this key area.</p>
        )}

        {!loading &&
          targets.map((t) => (
            <div
              key={t.target_id}
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-[11px] font-mono text-[#0f5b57] font-semibold">{t.target_id}</p>
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusColor(
                    t.status_band
                  )}`}
                >
                  {t.progress_pct != null ? `${t.progress_pct}%` : 'n/a'}
                </span>
              </div>
              <p className="text-xs text-gray-800 leading-snug line-clamp-3 mb-1.5">
                {t.indicator || t.target_description || t.target_id}
              </p>
              <ProgressBar pct={t.progress_pct} />
              <p className="text-[10px] text-gray-500 mt-1">
                {t.result_to_date != null ? String(t.result_to_date) : '—'} /{' '}
                {t.program_target != null ? String(t.program_target) : '—'}
                {t.aggregation ? ` · ${t.aggregation}` : ''}
              </p>
            </div>
          ))}
      </div>
    </motion.aside>
  );
};

export default KeyAreaTargetsPanel;
